import type { WozCaseEntity } from '../../woz-case/entities';
import type { WozCasePersistenceMode } from '../../woz-case/woz-case.types';
import type { WozCaseStorageAdapter } from './woz-case-storage.adapter';
import { WozCaseStorageLocalAdapter } from './woz-case-storage-local.adapter';
import { WozCaseStorageDatabaseAdapter } from './woz-case-storage-database.adapter';

/**
 * Unified storage service for a single WOZ case.
 *
 * Storage strategy:
 *   source = 'local'    -> write to localStorage only
 *   source = 'database' -> write to localStorage (fast) AND database (on demand)
 *
 * `save()` always writes to localStorage for immediate persistence.
 * `saveToDatabase()` is called explicitly on meaningful milestones
 * (promote, step completion) to persist to the backend.
 */
export class WozCaseStorageService {
  private adapter: WozCaseStorageAdapter;

  private static readonly localAdapter = new WozCaseStorageLocalAdapter();

  private static readonly databaseAdapter = new WozCaseStorageDatabaseAdapter();

  constructor(source: WozCasePersistenceMode) {
    this.adapter = source === 'local' ? WozCaseStorageService.localAdapter : WozCaseStorageService.databaseAdapter;
  }

  /** Run one-time initialization on the local adapter (e.g. version migration). */
  async init(): Promise<void> {
    await WozCaseStorageService.localAdapter.init();
  }

  /** Switch the active adapter (e.g. after promoting local -> database). */
  switchAdapter(source: WozCasePersistenceMode): void {
    this.adapter = source === 'local' ? WozCaseStorageService.localAdapter : WozCaseStorageService.databaseAdapter;
  }

  /**
   * Always writes to localStorage for fast, synchronous persistence.
   * This is the hot path called on every state update.
   */
  save(entity: WozCaseEntity): Promise<WozCaseEntity> {
    return WozCaseStorageService.localAdapter.save(entity);
  }

  /**
   * Persist the case to the database. Called on meaningful milestones:
   * - promote (first save after auth transition)
   * - flow step completion
   */
  saveToDatabase(entity: WozCaseEntity): Promise<WozCaseEntity> {
    return WozCaseStorageService.databaseAdapter.save(entity);
  }

  load(caseId?: string): Promise<WozCaseEntity | null> {
    return this.adapter.load(caseId);
  }

  fetchOpen(): Promise<WozCaseEntity[]> {
    return this.adapter.fetchOpen();
  }

  remove(caseId?: string): Promise<void> {
    return this.adapter.remove(caseId);
  }

  clear(): Promise<void> {
    return WozCaseStorageService.localAdapter.clear();
  }
}
